import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Search, Filter, CheckCircle, XCircle } from 'lucide-react';
import { n8nWebhookLogsService } from '../../../services/n8nWebhookLogsService';

export default function DeadLetterQueuePanel() {
  const [dlqEntries, setDlqEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    containerId: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadDLQ();
  }, []);

  const loadDLQ = async () => {
    try {
      setLoading(true);
      setError('');
      const entries = await n8nWebhookLogsService?.getDeadLetterQueue(filters);
      setDlqEntries(entries);
    } catch (err) {
      setError(`Erreur chargement DLQ: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async (dlqId) => {
    try {
      setReprocessing(dlqId);
      setError('');
      setSuccess('');

      const result = await n8nWebhookLogsService?.reprocessFromDeadLetterQueue(dlqId);
      
      if (result?.status === 'success') {
        setSuccess(`✅ ${result?.message}`);
        // Remove from list if successful
        setDlqEntries(prev => prev?.filter(entry => entry?.id !== dlqId));
      } else {
        setError(`⚠️ ${result?.message}`);
      }

      await loadDLQ(); // Reload list
    } catch (err) {
      setError(`Erreur retraitement: ${err?.message}`);
    } finally {
      setReprocessing(null);
    }
  };

  const handleDelete = async (dlqId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette entrée définitivement ?')) {
      return;
    }

    try {
      // Note: Add delete method to service if needed
      setError('Fonction de suppression non implémentée');
    } catch (err) {
      setError(`Erreur suppression: ${err?.message}`);
    }
  };

  const applyFilters = async () => {
    await loadDLQ();
  };

  const clearFilters = () => {
    setFilters({
      containerId: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
  };

  const filteredEntries = dlqEntries?.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm?.toLowerCase();
    return (
      entry?.id?.toLowerCase()?.includes(search) ||
      entry?.container?.containerCode?.toLowerCase()?.includes(search) ||
      entry?.reason?.toLowerCase()?.includes(search) ||
      entry?.errorMessage?.toLowerCase()?.includes(search)
    );
  });

  if (loading && !dlqEntries?.length) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Dead Letter Queue</h2>
              <p className="text-sm text-gray-600">
                Webhooks échoués définitivement en attente de retraitement manuel
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
              {filteredEntries?.length} entrée(s)
            </span>
            <button
              onClick={loadDLQ}
              disabled={loading}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <XCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 text-sm">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                placeholder="Rechercher dans DLQ..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="w-5 h-5 mr-2" />
            Filtres
          </button>
        </div>

        {showFilters && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID Conteneur
                </label>
                <input
                  type="text"
                  value={filters?.containerId}
                  onChange={(e) => setFilters(prev => ({ ...prev, containerId: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date début
                </label>
                <input
                  type="date"
                  value={filters?.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date fin
                </label>
                <input
                  type="date"
                  value={filters?.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DLQ Entries Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Conteneur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Raison
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Retry Count
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Déplacé le
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Erreur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEntries?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>Aucune entrée dans la Dead Letter Queue</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Les webhooks échoués définitivement apparaîtront ici
                  </p>
                </td>
              </tr>
            ) : (
              filteredEntries?.map((entry) => (
                <tr key={entry?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-xs font-mono text-gray-600">
                      {entry?.id?.substring(0, 8)}...
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {entry?.container?.containerCode || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {entry?.container?.status || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {entry?.reason}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                      {entry?.retryCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(entry?.movedAt)?.toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(entry?.movedAt)?.toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {entry?.errorMessage || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleReprocess(entry?.id)}
                        disabled={reprocessing === entry?.id}
                        className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${reprocessing === entry?.id ? 'animate-spin' : ''}`} />
                        Retraiter
                      </button>
                      <button
                        onClick={() => handleDelete(entry?.id)}
                        disabled={reprocessing === entry?.id}
                        className="flex items-center px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <p className="text-sm text-gray-600">
          💡 <strong>Conseil:</strong> Les entrées DLQ nécessitent une intervention manuelle.
          Vérifiez la raison de l'échec avant de retraiter.
        </p>
      </div>
    </div>
  );
}